'use client';

import { type FC, type HTMLAttributes, useEffect, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/nextjs';
import { useSearchParams } from 'next/navigation';
import type { z } from 'zod';

import { getConsentData } from '@/services/consents';
import { checkDiscountCode, checkReferralCode } from '@/services/promo-codes';
import {
    patchCodeRegistrationData,
    postActivateEmail,
    postEmailData,
    putConsentData,
} from '@/services/registration';
import type {
    ConsentType,
    PostEmailErrorDataType,
} from '@/services/registration/types';

import InfoModal from '@/components/organisms/RegistrationForm/components/InfoModal';
import RegistrationFormComponent from '@/components/organisms/RegistrationForm/components/RegistrationFormComponent';
import type {
    InfoModalContentType,
    MappedConsentType,
} from '@/components/organisms/RegistrationForm/RegistrationForm.types';
import { formatConsentData } from '@/components/organisms/RegistrationForm/utils/consentsHelpers';
import { getInfoModalConfig } from '@/components/organisms/RegistrationForm/utils/GetInfoModalConfig';
import type { RegistrationFormSchema } from '@/components/organisms/RegistrationForm/utils/RegistrationFormSchema';
import { createValidationSchemaWithConsents } from '@/components/organisms/RegistrationForm/utils/RegistrationFormSchema';
import type { FormConfigType } from '@/components/templates/RegisterFormContent/RegisterFormContent';

import { env } from '@/env.mjs';

interface RegistrationFormProps extends HTMLAttributes<HTMLFormElement> {
    formName: string;
    isSubmitButtonSticky?: boolean;
    formConfig?: FormConfigType[];
}

const RegistrationForm: FC<RegistrationFormProps> = ({
    formName,
    formConfig,
    isSubmitButtonSticky,
    ...props
}) => {
    const [consentsAvailable, setConsentsAvailable] = useState<boolean>(true);
    const [modalContent, setModalContent] = useState<InfoModalContentType>(
        {} as InfoModalContentType,
    );
    const [registrationConsents, setRegistrationConsents] = useState<
        MappedConsentType[]
    >([] as MappedConsentType[]);
    const [promoCodesConsent, setPromoCodesConsent] =
        useState<MappedConsentType>({} as MappedConsentType);
    const [isLoadingConsents, setIsLoadingConsents] = useState<boolean>(true);

    const searchParams = useSearchParams();

    const discountCodeParam = searchParams.get('discountCode') || '';
    const referralCodeParam = searchParams.get('ref') || '';

    const formInitialValues: { [key in string]: any } = {
        email: '',
        phoneNumber: '',
        userCode: discountCodeParam || referralCodeParam,
        agreementsAcceptance: false,
        captcha: '',
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                await getConsentData('registration').then(data => {
                    const mappedConsents: MappedConsentType[] = [];

                    // find promo code consent and create array with the rest
                    data.forEach(consent => {
                        const consentData = formatConsentData(
                            consent,
                        ) as MappedConsentType;

                        if (consentData.slug === 'promocode_reg') {
                            setPromoCodesConsent(consentData);
                        } else {
                            mappedConsents.push(consentData);
                        }

                        // add every consent slug to initial values
                        formInitialValues[consentData.slug] = false;
                    });

                    setRegistrationConsents(mappedConsents);

                    setIsLoadingConsents(false);
                });
            } catch (error) {
                const errData = await error;

                Sentry.captureException({
                    message: 'error when getting consents content',
                    endpoint:
                        '/v3/consents?filter[groupName][]=${consentGroup}',
                    errorData: errData,
                });

                setConsentsAvailable(!consentsAvailable);
            }
        };
        fetchData();
    }, []);

    const formProviderConfig = useForm<z.infer<typeof RegistrationFormSchema>>({
        mode: 'all',
        resolver: zodResolver(
            createValidationSchemaWithConsents(registrationConsents),
        ),
        defaultValues: formInitialValues,
    });

    const { setError } = formProviderConfig;

    const handleInfoModalClose = () => {
        setModalContent({} as InfoModalContentType);
    };

    const INFO_MODAL_CONFIG = getInfoModalConfig(handleInfoModalClose);

    const changeLocationToRegisterVerification = (
        discountCode: string | undefined,
        referralCode: string | undefined,
    ) => {
        let href = `${env.NEXT_PUBLIC_PANEL_URL}/registration/sms-code`;
        href += `?uuid=${sessionStorage.getItem('uuid')}`;

        if (discountCode) {
            href += `&discountCode=${discountCode}`;
        } else if (referralCode) {
            href += `&ref=${referralCode}`;
        }

        window.location.href = href;
    };

    const handleError = (email: string, error: PostEmailErrorDataType) => {
        const { reason, code, errors } = error;

        switch (code) {
            case 409:
                if (reason === 'existingUserStatusPermitted') {
                    postActivateEmail({ email }).then(() => {
                        setModalContent(INFO_MODAL_CONFIG[reason]);
                    });
                } else if (
                    [
                        'existingUserStatusToTransaction',
                        'existingUserStatusRejected',
                        'existingUser',
                        'conflictData',
                    ].includes(reason)
                ) {
                    setModalContent(INFO_MODAL_CONFIG[reason]);
                }

                break;

            case 400:
                if (errors.some(({ path }) => path === 'email')) {
                    setError('email', {
                        type: 'custom',
                        message: 'Adres email nie jest poprawny.',
                    });
                } else if (errors.some(({ path }) => path === 'phone')) {
                    setError('phoneNumber', {
                        type: 'custom',
                        message:
                            'Numer telefonu nie jest poprawnym numerem mobilnym.',
                    });
                } else if (errors.some(({ path }) => path === 'discountCode')) {
                    setError('userCode', {
                        type: 'custom',
                        message: 'Kod promocyjny nie jest poprawny.',
                    });
                } else if (errors.some(({ path }) => path === 'referralCode')) {
                    setError('userCode', {
                        type: 'custom',
                        message: 'Kod promocyjny nie jest poprawny.',
                    });
                }

                break;

            default:
                Sentry.captureException({
                    message: 'error on registration',
                    endpoint:
                        '${env.NEXT_PUBLIC_ORDER_API_URL}/v3/registrations',
                    errorData: error,
                });
        }
    };

    const handleSubmit = async (
        data: z.infer<typeof RegistrationFormSchema>,
    ) => {
        const { userCode, email, phoneNumber, captcha } = data;

        const selectedConsents: ConsentType[] = [];

        registrationConsents.forEach(({ slug, consentVersionId }) => {
            if (data[slug as keyof z.infer<typeof RegistrationFormSchema>]) {
                selectedConsents.push({
                    consentVersionId,
                    value: true,
                });
            }
        });

        if (!!userCode?.length) {
            selectedConsents.push({
                consentVersionId: promoCodesConsent.consentVersionId,
                value: true,
            });
        }

        setModalContent({} as InfoModalContentType);

        const sessionStorageUuid = sessionStorage.getItem('uuid');

        const isRegisterSessionExisting =
            !!sessionStorageUuid &&
            email === sessionStorage.getItem('email') &&
            phoneNumber === sessionStorage.getItem('phoneNumber');

        const parameterBag = { formName, contractVersion: 2 };

        let discountCode = '';
        let referralCode = '';

        if (userCode) {
            await checkDiscountCode(userCode)
                .then(() => {
                    discountCode = userCode;
                })
                .catch(error => {
                    if (error.status === 404) {
                        return checkReferralCode(userCode);
                    }
                })
                .then(() => {
                    referralCode = userCode;
                })
                .catch(error => {
                    console.warn(error);
                });
        }

        if (isRegisterSessionExisting) {
            await patchCodeRegistrationData(
                sessionStorageUuid,
                discountCode,
                referralCode,
                parameterBag,
            )
                .then(() => {
                    if (userCode) {
                        sessionStorage.setItem('discountCode', userCode);
                        sessionStorage.setItem('referralCode', userCode);
                    }

                    return changeLocationToRegisterVerification(
                        discountCode,
                        referralCode,
                    );
                })
                .catch(async error => {
                    handleError(email, await error);
                });
        } else {
            await postEmailData(email, phoneNumber, captcha)
                .then(({ uuid }) => {
                    putConsentData(selectedConsents, uuid);
                    sessionStorage.setItem('uuid', uuid);
                    sessionStorage.setItem('phoneNumber', phoneNumber);
                    sessionStorage.setItem('email', email);

                    return patchCodeRegistrationData(
                        uuid,
                        discountCode,
                        referralCode,
                        parameterBag,
                    );
                })
                .then(() => {
                    if (userCode) {
                        sessionStorage.setItem('discountCode', userCode);
                        sessionStorage.setItem('referralCode', userCode);
                    }

                    return changeLocationToRegisterVerification(
                        discountCode,
                        referralCode,
                    );
                })
                .catch(async error => {
                    handleError(email, await error);
                });
        }
    };

    return (
        <GoogleReCaptchaProvider reCaptchaKey={env.NEXT_PUBLIC_RECAPTCHA_KEY}>
            <RegistrationFormComponent
                formConfig={formConfig}
                handleSubmit={handleSubmit}
                formProviderConfig={formProviderConfig}
                registrationConsents={registrationConsents}
                promoCodesConsent={promoCodesConsent}
                isLoadingConsents={isLoadingConsents}
                isSubmitButtonSticky={isSubmitButtonSticky}
                formName={formName}
                consentsAvailable={consentsAvailable}
                {...props}
            />

            <InfoModal
                isVisible={!!Object.keys(modalContent).length}
                handleClose={handleInfoModalClose}
                modalContent={modalContent}
            />
        </GoogleReCaptchaProvider>
    );
};

export default RegistrationForm;
